import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Reveal from '@/components/feature/Reveal';

export interface ServiceBlock {
  icon: string;
  title: string;
  desc: string;
  items?: string[];
}

export interface ServiceContent {
  eyebrow: string;
  title: string;
  subtitle: string;
  tags?: string[];
  blocks: ServiceBlock[];
  highlightTitle?: string;
  highlightDesc?: string;
}

export default function ServicePage({ content }: { content: ServiceContent }) {
  return (
    <div className="min-h-screen bg-background-950">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-primary-500/12 blur-[130px]"></div>
            <div className="absolute bottom-0 right-0 w-[400px] h-[300px] rounded-full bg-accent-500/10 blur-[120px]"></div>
          </div>
          <div className="relative mx-auto max-w-4xl px-4 md:px-6 text-center">
            <Reveal>
              <span className="inline-block text-xs md:text-sm font-semibold tracking-widest uppercase text-primary-400">
                {content.eyebrow}
              </span>
              <h1 className="mt-4 text-4xl md:text-5xl font-bold font-heading text-foreground-950 leading-tight tracking-tight">
                {content.title}
              </h1>
              <p className="mt-5 text-base md:text-lg text-foreground-500 leading-relaxed">
                {content.subtitle}
              </p>
              {content.tags && (
                <div className="mt-7 flex flex-wrap justify-center gap-2">
                  {content.tags.map((t) => (
                    <span
                      key={t}
                      className="text-xs md:text-sm text-foreground-400 px-3 py-1.5 rounded-md bg-background-800/50 border border-background-700/50"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </Reveal>
          </div>
        </section>

        {/* Blocks */}
        <section className="pb-20 md:pb-28">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {content.blocks.map((b, i) => (
                <Reveal key={b.title} delay={i * 70}>
                  <div className="h-full rounded-lg border border-background-700/60 bg-background-900/50 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-primary-500/40">
                    <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-background-800 border border-background-700/60 text-primary-400">
                      <i className={`${b.icon} text-xl`}></i>
                    </div>
                    <h2 className="mt-4 text-lg font-bold font-heading text-foreground-950">
                      {b.title}
                    </h2>
                    <p className="mt-2 text-sm text-foreground-500 leading-relaxed">
                      {b.desc}
                    </p>
                    {b.items && (
                      <ul className="mt-4 space-y-2">
                        {b.items.map((it) => (
                          <li
                            key={it}
                            className="flex items-start gap-2 text-sm text-foreground-400"
                          >
                            <i className="ri-check-line text-secondary-400 mt-0.5"></i>
                            {it}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>

            {content.highlightTitle && (
              <Reveal className="mt-14">
                <div className="rounded-lg border border-accent-500/30 bg-gradient-to-br from-accent-500/10 to-primary-500/5 p-8 md:p-10 text-center">
                  <h3 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                    {content.highlightTitle}
                  </h3>
                  {content.highlightDesc && (
                    <p className="mt-4 text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                      {content.highlightDesc}
                    </p>
                  )}
                  <Link
                    to="/contacto"
                    className="mt-7 inline-flex items-center gap-2 whitespace-nowrap font-medium text-background-950 bg-primary-500 hover:bg-primary-600 rounded-md px-7 py-3.5 transition-colors cursor-pointer glow-primary"
                  >
                    Hablemos de tu proyecto
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </Reveal>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}